import * as XLSX from "xlsx";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_BACK_END_API;

export interface ProfanityResultRow {
  key: number;
  timecode: string;
  start_time: string;
  end_time: string;
  severity: string;
  confidence: number;
  bleep: boolean;
  ai_rechecked: boolean;
  term: string;
  textSnippet: string;
  bleep_category: string
}

interface ProfanityResult {
  bleep_detected: boolean;
  bleep_category: string;
  confidence: number;
  cue_index: number;
  end_time: string;
  is_flagged: boolean;
  ai_rechecked: boolean;
  match_term: string | null;
  normalized_text: string;
  original_text: string;
  severity: string;
  start_time: string;
}

export const processProfanityFile = async (
  file: File,
  reprocessForBleeps: boolean,
): Promise<{
  allData: ProfanityResultRow[];
  flaggedData: ProfanityResultRow[];
}> => {
  const formData = new FormData();
  formData.append("file", file);
  if (reprocessForBleeps) {
    formData.append("reprocessForBleeps", "reprocessForBleeps");
  }

  const response = await axios.post(
    `${API_BASE_URL}/profanity/scan`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    }
  );

  

  const { results } = response.data as {
    results: ProfanityResult[];
  };

  const flaggedData: ProfanityResultRow[] = [];
  const allData: ProfanityResultRow[] = [];
  results.forEach((r, index) => {
    const tempObj = {
      key: index + 1,
      timecode: r.start_time + " - " + r.end_time,
      start_time: r.start_time,
      end_time: r.end_time,
      severity: r.severity,
      confidence: r.confidence,
      bleep: r.bleep_detected,
      term: r.match_term || "",
      textSnippet: r.original_text,
      bleep_category: r.bleep_category,
      ai_rechecked: r.ai_rechecked,
    }
    if (r.is_flagged || r.bleep_detected) {
      flaggedData.push(tempObj)
    }
    allData.push(tempObj)
  });
  return {flaggedData, allData}
};

export const exportProfanityResultToCSV = (inputFileName: string, data: Array<ProfanityResultRow>) => {
    const headers: string[] = [
        "Start Time",
        "End Time",
        "Severity",
        "Matched Term",
        "Original Text",
        "Confidence",
        "Bleep Detected",
        "Bleep Category"
    ];

    const finalExportData: (string | number)[][] = data.map(item => [
        item.start_time,
        item.end_time,
        item.severity,
        item.term,
        item.textSnippet
            .replace(/"/g, '""')           // escape double quotes for CSV
            .replace(/[\r\n\t\b\f\v\\]/g, ' '), // remove all common escape/control characters
        Math.round(item.confidence * 100),
        item.bleep ? "Yes" : "No",
        item.ai_rechecked ? `AI: ${item.bleep_category}` : item.bleep_category,
    ]);

    // Generate counts for each severity
    const severityCounts = { slurs: 0, high: 0, medium: 0, low: 0 };
    data.forEach(item => {
        const sev = item.severity?.toLowerCase();
        if (sev === "slurs") severityCounts.slurs++;
        else if (sev === "high") severityCounts.high++;
        else if (sev === "medium") severityCounts.medium++;
        else if (sev === "low") severityCounts.low++;
    });

    // Extract the filename without extension
    const lastDotIdx = inputFileName.lastIndexOf(".");
    const baseFilename =
        lastDotIdx !== -1
            ? inputFileName.slice(0, lastDotIdx)
            : inputFileName;

    // Format output filename as requested
    const outputFilename = `${baseFilename}-profanity-S${severityCounts.slurs}-H${severityCounts.high}-M${severityCounts.medium}-L${severityCounts.low}.csv`;

    const worksheetData = [headers, ...finalExportData];

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Profanity Analytics");
    XLSX.writeFile(wb, outputFilename);
};